import React, { useState, useEffect, useRef, useContext } from "react";
import { unstable_batchedUpdates } from "react-dom";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, formatDistanceToNow } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip, CircularProgress } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";
import { parseVcard, parseVcardsArray } from "../../utils/parseVcard";
import { emitLocalTicketUpdate, emitLocalTicketDelete } from "../../utils/ticketUtils";

import AndroidIcon from "@material-ui/icons/Android";
import VisibilityIcon from "@material-ui/icons/Visibility";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckIcon from "@material-ui/icons/Check";
import UndoRoundedIcon from "@material-ui/icons/UndoRounded";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import TicketMessagesDialog from "../TicketMessagesDialog";
import ChannelAvatar, { getTicketChannel } from "../ChannelAvatar";
import contrastColor from "../../helpers/contrastColor";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ScheduleModal from "../ScheduleModal";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 14,
    paddingRight: 8,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&.Mui-selected": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(45,123,232,0.18)"
        : "#EAF2FD",
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
      "&:hover": {
        backgroundColor: theme.palette.type === "dark"
          ? "rgba(255,255,255,0.1)"
          : "rgba(37,118,210,0.12)",
      },
    },
  },

  pendingTicket: {
    cursor: "unset",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  lastMessageTime: {
    flexShrink: 0,
    fontSize: "0.72rem",
    marginLeft: 6,
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },

  contactLastMessage: {
    flex: 1,
    minWidth: 0,
  },

  badgeStyle: {
    color: "white",
    backgroundColor: theme.palette.primary.main,
  },

  unreadBadge: {
    flexShrink: 0,
    marginLeft: 6,
    alignSelf: "center",
  },

  timeUnread: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  lastMessageUnread: {
    color: `${theme.palette.text.primary} !important`,
  },

  queueDot: {
    display: "inline-block",
    width: 7,
    height: 7,
    borderRadius: "50%",
    marginRight: 4,
    flexShrink: 0,
  },

  // Botão redondo só com ícone: ocupa bem menos que "ACEITAR" e libera
  // espaço para a prévia da mensagem nos atendimentos aguardando.
  acceptButton: {
    width: 24,
    height: 24,
    padding: 0,
    marginLeft: 6,
    flexShrink: 0,
    alignSelf: "center",
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    "&.Mui-disabled": {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      opacity: 0.7,
    },
  },

  optionsButton: {
    padding: 4,
    marginLeft: 2,
    flexShrink: 0,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
    },
  },

  secondaryRow: {
    display: "flex",
    alignItems: "center",
  },

  // O nome do contato tem prioridade: ocupa a largura natural até 55% da
  // linha. O chip da fila fica com o espaço restante e só corta quando faltar.
  contactName: {
    flex: "0 1 auto",
    minWidth: 0,
    fontWeight: 600,
    fontSize: "0.86rem",
  },

  nameSpacer: {
    flex: 1,
  },

  tagsRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 5,
    overflow: "hidden",
  },

  tagChip: {
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: 110,
    padding: "1px 7px",
    borderRadius: 999,
    fontSize: "0.66rem",
    fontWeight: 500,
    lineHeight: "15px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  tagMore: {
    flexShrink: 0,
    padding: "1px 6px",
    borderRadius: 999,
    fontSize: "0.62rem",
    fontWeight: 600,
    lineHeight: "15px",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.selected,
  },

  // Departamento: chip neutro com um ponto na cor da fila
  queueChip: {
    flex: "0 1 auto",
    minWidth: 0,
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 7px",
    borderRadius: 999,
    fontSize: "0.66rem",
    fontWeight: 500,
    lineHeight: "15px",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#F0F3F8",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

// Texto preto/branco conforme a cor de fundo (fila/etiqueta); cores fora do
// formato #rrggbb caem no branco para não quebrar o cálculo de contraste.
const chipTextColor = (bg) => (HEX_COLOR.test(bg) ? contrastColor(bg) : "#ffffff");

// Quantas etiquetas cabem na linha antes de agrupar o resto em "+N".
const MAX_VISIBLE_TAGS = 3;

const TicketListItemCustom = ({ ticket, onTicketClick }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [ticketQueueColor, setTicketQueueColor] = useState(null);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { profile } = user;

  useEffect(() => {
    setTicketQueueColor(ticket.queue?.color || "#7C7C7C");
    return () => {
      isMounted.current = false;
    };
  }, [ticket]);

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });

      // Atualiza as listas localmente com o ticket retornado pelo PUT, sem
      // depender do socket (que pode estar morto após a aba ficar em segundo
      // plano). Move o ticket de "Aguardando" para "Atendendo" na hora.
      if (data?.id) emitLocalTicketUpdate(data);

      let settingIndex;
      try {
        const { data } = await api.get("/settings/");
        settingIndex = data.filter((s) => s.key === "sendGreetingAccepted");
      } catch (err) {
        toastError(err);
      }

      if (settingIndex && settingIndex.length > 0 && settingIndex[0].value === "enabled" && !ticket.isGroup) {
        await handleSendMessage(ticket.id);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    unstable_batchedUpdates(() => {
      if (isMounted.current) setLoading(false);
      if (onTicketClick) onTicketClick();
      history.push(`/tickets/${ticket.uuid}`);
    });
  };

  const handleSendMessage = async (id) => {
    const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`;
    try {
      await api.post(`/messages/${id}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: `*Mensagem Automática:*\n${msg.trim()}`,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleResolveTicket = async () => {
    setLoading(true);
    try {
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        status: "closed",
        userId: user?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null,
      });
      unstable_batchedUpdates(() => {
        if (data?.id) emitLocalTicketUpdate(data);
        history.push("/tickets");
      });
    } catch (err) {
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
  };

  const handleReturnTicket = async () => {
    try {
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        status: "pending",
        userId: null,
        useIntegration: ticket.useIntegration,
        promptId: ticket.promptId,
        integrationId: ticket.integrationId,
      });
      unstable_batchedUpdates(() => {
        if (data?.id) emitLocalTicketUpdate(data);
        history.push("/tickets");
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${ticket.id}`);
      emitLocalTicketDelete(ticket.id);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSelectTicket = (ticket) => {
    if (onTicketClick) onTicketClick();
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleOpenMenu = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const getLastMessagePreview = (raw) => {
    if (!raw) return raw;
    if (raw.includes("data:image/png;base64")) return "Localização";
    if (raw.startsWith("BEGIN:VCARD")) {
      const contact = parseVcard(raw);
      return contact ? `📇 ${contact.name}` : "📇 Contato";
    }
    if (raw.startsWith("[")) {
      const contacts = parseVcardsArray(raw);
      if (contacts.length > 0) {
        return contacts.length === 1
          ? `📇 ${contacts[0].name}`
          : `📇 ${contacts.length} contatos`;
      }
    }
    return raw;
  };

  const lastMessagePreview = getLastMessagePreview(ticket.lastMessage);

  const tags = Array.isArray(ticket.tags) ? ticket.tags : [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTags = tags.slice(MAX_VISIBLE_TAGS);

  return (
    <React.Fragment key={ticket.id}>
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      />
      <ConfirmationModal
        title={`Deletar ticket #${ticket.id} de ${ticket.contact.name}?`}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteTicket}
      >
        {i18n.t("ticketOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <TransferTicketModalCustom
        modalOpen={transferModalOpen}
        onClose={() => { if (isMounted.current) setTransferModalOpen(false); }}
        ticketid={ticket.id}
      />
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={() => { setScheduleModalOpen(false); }}
        aria-labelledby="form-dialog-title"
        contactId={ticket.contact?.id}
      />

      <ListItem
        dense
        button
        onClick={() => {
          if (ticket.status === "pending") return;
          handleSelectTicket(ticket);
        }}
        selected={Boolean(ticketId && (ticketId === ticket.uuid || +ticketId === ticket.id))}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        <ListItemAvatar>
          <ChannelAvatar
            channel={getTicketChannel(ticket)}
            style={{ width: 42, height: 42 }}
            src={ticket?.contact?.profilePicUrl}
            name={ticket?.contact?.name}
          />
        </ListItemAvatar>

        <ListItemText
          disableTypography
          primary={
            <>
              <span className={classes.contactNameWrapper}>
                <Typography
                  noWrap
                  component="span"
                  variant="body2"
                  color="textPrimary"
                  className={classes.contactName}
                >
                  {ticket.contact.name}
                  {ticket.chatbot && (
                    <Tooltip title="Chatbot">
                      <AndroidIcon
                        fontSize="small"
                        style={{ color: grey[500], marginLeft: 4, verticalAlign: "middle", fontSize: 14 }}
                      />
                    </Tooltip>
                  )}
                </Typography>
                <span className={classes.nameSpacer} />
                {profile === "admin" && (
                  <Tooltip title="Espiar Conversa">
                    <VisibilityIcon
                      onClick={(e) => { e.stopPropagation(); setOpenTicketMessageDialog(true); }}
                      fontSize="small"
                      style={{ color: blue[400], cursor: "pointer", marginLeft: 4, flexShrink: 0, fontSize: 14 }}
                    />
                  </Tooltip>
                )}
                {ticket.lastMessage && (
                  <Tooltip
                    title={format(parseISO(ticket.updatedAt), "dd/MM/yyyy HH:mm")}
                    placement="left"
                  >
                    <span
                      className={clsx(classes.lastMessageTime, {
                        [classes.timeUnread]: ticket.unreadMessages > 0,
                      })}
                    >
                      {formatDistanceToNow(parseISO(ticket.updatedAt), {
                        addSuffix: false,
                        locale: ptBR,
                      })}
                    </span>
                  </Tooltip>
                )}
              </span>
            </>
          }
          secondary={
            <>
            <span className={classes.secondaryRow}>
              <Typography
                className={clsx(classes.contactLastMessage, {
                  [classes.lastMessageUnread]: ticket.unreadMessages > 0,
                })}
                noWrap
                component="span"
                variant="body2"
                color="textSecondary"
                style={{ fontSize: "0.8rem" }}
              >
                <MarkdownWrapper>{lastMessagePreview}</MarkdownWrapper>
              </Typography>

              {ticket.unreadMessages > 0 && (
                <Badge
                  className={classes.unreadBadge}
                  badgeContent={ticket.unreadMessages}
                  classes={{ badge: classes.badgeStyle }}
                />
              )}

              {ticket.status === "pending" && (
                <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
                  <span>
                    <IconButton
                      size="small"
                      className={classes.acceptButton}
                      disabled={loading}
                      aria-label={i18n.t("ticketsList.buttons.accept")}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleAcepptTicket(ticket.id);
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CheckIcon style={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              <IconButton
                className={classes.optionsButton}
                size="small"
                onClick={handleOpenMenu}
              >
                <KeyboardArrowDownIcon style={{ fontSize: 16 }} />
              </IconButton>
            </span>
              <span className={classes.tagsRow}>
                <Tooltip title={ticket.queue?.name ? `Departamento: ${ticket.queue.name}` : "Sem departamento"}>
                  <span className={classes.queueChip}>
                    <span
                      className={classes.queueDot}
                      style={{ backgroundColor: ticketQueueColor || "#7C7C7C" }}
                    />
                    {ticket.queue?.name || "Sem departamento"}
                  </span>
                </Tooltip>

                  {visibleTags.map((tag) => (
                    <Tooltip key={tag.id} title={tag.name}>
                      <span
                        className={classes.tagChip}
                        style={{
                          backgroundColor: tag.color || "#7C7C7C",
                          color: chipTextColor(tag.color || "#7C7C7C"),
                        }}
                      >
                        {tag.name}
                      </span>
                    </Tooltip>
                  ))}
                  {hiddenTags.length > 0 && (
                    <Tooltip title={hiddenTags.map((t) => t.name).join(", ")}>
                      <span className={classes.tagMore}>+{hiddenTags.length}</span>
                    </Tooltip>
                  )}
              </span>
            </>
          }
        />
      </ListItem>

      <Menu
        anchorEl={menuAnchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        {ticket.status === "open" && (
          <Tooltip title="Marcar atendimento como resolvido" placement="left">
            <MenuItem onClick={() => { handleResolveTicket(); handleCloseMenu(); }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="primary" />
              </ListItemIcon>
              Resolver
            </MenuItem>
          </Tooltip>
        )}
        {ticket.status === "open" && (
          <Tooltip title="Devolver ticket para a fila de pendentes" placement="left">
            <MenuItem onClick={() => { handleReturnTicket(); handleCloseMenu(); }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <UndoRoundedIcon fontSize="small" />
              </ListItemIcon>
              {i18n.t("messagesList.header.buttons.return")}
            </MenuItem>
          </Tooltip>
        )}
        <Tooltip title="Agendar mensagem ou lembrete para este contato" placement="left">
          <MenuItem onClick={() => { handleCloseMenu(); setScheduleModalOpen(true); }}>
            <ListItemIcon style={{ minWidth: 32 }}>
              <EventAvailableIcon fontSize="small" />
            </ListItemIcon>
            {i18n.t("ticketOptionsMenu.schedule")}
          </MenuItem>
        </Tooltip>
        <Tooltip title="Transferir atendimento para outro atendente ou departamento" placement="left">
          <MenuItem onClick={() => { handleCloseMenu(); setTransferModalOpen(true); }}>
            <ListItemIcon style={{ minWidth: 32 }}>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            {i18n.t("ticketOptionsMenu.transfer")}
          </MenuItem>
        </Tooltip>
        {profile === "admin" && (
          <Tooltip title="Excluir permanentemente este ticket" placement="left">
            <MenuItem onClick={() => { handleCloseMenu(); setConfirmationOpen(true); }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              <span style={{ color: "red" }}>{i18n.t("ticketOptionsMenu.delete")}</span>
            </MenuItem>
          </Tooltip>
        )}
      </Menu>

    </React.Fragment>
  );
};

export default TicketListItemCustom;
